import { captureException } from '@sentry/react';
import { geoAlbersUsa, geoPath } from 'd3-geo';
import { useContext, useEffect, useRef } from 'react';
import { feature } from 'topojson-client';
import { Topology } from 'topojson-specification';

import { MapContext } from '../context';
import {
	allPoliciesSelected,
	drawCountiesCanvas,
	formatter,
	showSumOfPolicies,
} from '../helpers';
import { useCountiesGeography } from '../hooks/useCountiesGeography';
import { useUSGeography } from '../hooks/useUSGeography';
import { ActiveStateOrUSId, StateId, TaxData } from '../types';
import HoverContainer from './HoverContainer';
import './USMap.scss';

const hoverText = (
	name: string,
	taxChange: number,
	year: number,
	jobsChange: number,
	showJobs = true,
) => `
  <p style="text-align:center; border-bottom: 1px solid #ddd; padding-bottom: 9px; margin-bottom: 9px;"><strong>${name}</strong></p>
  <table><tbody>
  <tr style="background:transparent">
    <td style="border: none">Average Tax ${taxChange <= 0 ? 'Cut' : 'Increase'} in ${year}:</td>
    <td style="border: none; text-align: right; white-space: nowrap; color: ${taxChange > 0 ? '#ef4438' : '#00aa22'}">${formatter(Math.abs(taxChange), '$')}</td>
  </tr>
	${
		showJobs
			? `<tr style="background:transparent">
					<td style="border: none">Estimated Long-Run Jobs ${jobsChange >= 0 ? 'Added' : 'Forgone'}:</td>
					<td style="border: none; text-align: right; white-space: nowrap; color: ${jobsChange < 0 ? '#ef4438' : '#00aa22'}">${formatter(Math.abs(jobsChange), ',')}</td>
				</tr>`
			: ''
	}

  </tbody></table>`;

interface USMapsProps {
	data: TaxData;
	year: number;
	updateActiveState: (stateId: ActiveStateOrUSId) => void;
	scale: number;
	xScale: number;
	yScale: number;
	domain: number[];
}

const USMap = ({
	data,
	year,
	updateActiveState,
	scale,
	xScale,
	yScale,
	domain,
}: USMapsProps) => {
	const {
		countiesGeography,
		countiesGeographyLoading,
		countiesGeographyError,
	} = useCountiesGeography();
	const { USGeography, USGeographyLoading, USGeographyError } =
		useUSGeography();

	if (
		(countiesGeographyLoading && !countiesGeography) ||
		(USGeographyLoading && !USGeography)
	) {
		return null;
	}

	if (
		countiesGeographyError ||
		!countiesGeography ||
		USGeographyError ||
		!USGeography
	) {
		return <p>Error loading map data.</p>;
	}

	return (
		<USMapRender
			data={data}
			year={year}
			updateActiveState={updateActiveState}
			scale={scale}
			xScale={xScale}
			yScale={yScale}
			domain={domain}
			us={USGeography}
			counties={countiesGeography}
		/>
	);
};

interface USMapRenderProps extends USMapsProps {
	us: Topology;
	counties: Topology;
}

const USMapRender = ({
	data,
	year,
	updateActiveState,
	scale,
	xScale,
	yScale,
	domain,
	us,
	counties,
}: USMapRenderProps) => {
	const { context } = useContext(MapContext);
	const canvasRef = useRef<HTMLCanvasElement>(null);

	const US = feature(us, us.objects.land);

	const path = geoPath().projection(
		geoAlbersUsa()
			.scale(scale)
			.translate([xScale / 2, yScale / 2 - 25]),
	);

	const countyFeatures = feature(
		counties,
		counties.objects.cb_2024_us_county_20m,
	);

	useEffect(() => {
		if (!canvasRef.current) return;
		drawCountiesCanvas({
			data,
			us: US,
			counties: countyFeatures,
			year,
			path,
			canvas: canvasRef.current,
			xScale,
			yScale,
			domain,
			context,
		});
	}, [canvasRef.current, context, year]);

	const states = feature(us, us.objects.states);

	if (!('features' in states)) {
		captureException(
			new Error('No features in states', {
				cause: states,
			}),
		);
		return null;
	}

	const statePaths = states.features.map(d => {
		if (!d.id) return null;
		const id = +d.id as StateId;
		const stateInfo = data[id];

		return (
			<path
				className="us-map__state"
				d={path(d) ?? undefined}
				data-tooltip-html={
					stateInfo
						? hoverText(
								stateInfo.name,
								showSumOfPolicies(stateInfo.data[year][0], context),
								year,
								stateInfo.jobs,
								allPoliciesSelected(context),
							)
						: null
				}
				data-tooltip-id="usmap"
				key={`state-${d.id}`}
				onClick={() => {
					updateActiveState(id);
				}}
			/>
		);
	});

	return (
		<div className="us-map__wrapper">
			<svg width="100%" viewBox={`0 0 ${xScale} ${yScale}`}>
				<defs>
					<path id="land" d={path(US) ?? undefined} />
				</defs>
				<clipPath id="clip-land">
					<use xlinkHref="#land" />
				</clipPath>
				<g>{statePaths}</g>
			</svg>
			<canvas
				width={xScale}
				height={yScale}
				id="us-canvas"
				ref={canvasRef}
				style={{ width: '100%' }}
			></canvas>
			<HoverContainer id="usmap" aria-haspopup="true" />
		</div>
	);
};

export default USMap;
